import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { useOfflineOperations } from '../hooks/useOfflineOperations';
import { dashboardCache } from '../services/dashboardCache';

interface KPICard {
  id: string;
  title: string;
  value: string | number;
  change: number;
  changeType: 'increase' | 'decrease' | 'neutral';
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  description: string;
}

interface DashboardData {
  activePlans: any;
  paymentsSummary: any;
  overdueAgeing: any;
  dailyVariance: any;
  onTimeRate: any;
}

const DashboardPage: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<string>('all');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });
  const [cacheStats, setCacheStats] = useState<any>(null);
  
  const { isOnline } = useOfflineOperations();

  // Fetch dashboard data with caching
  const fetchDashboardData = async (useCache: boolean = true) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        start_date: dateRange.start,
        end_date: dateRange.end,
        branch_id: selectedBranch !== 'all' ? selectedBranch : '',
      };

      // Check cache first if enabled
      if (useCache) {
        const cachedData = dashboardCache.get<DashboardData>('dashboard-data');
        if (cachedData) {
          setDashboardData(cachedData);
          setLoading(false);
          return;
        }
      }

      // Fetch from API
      const startTime = performance.now();
      
      const [activePlans, paymentsSummary, overdueAgeing, dailyVariance, onTimeRate] = await Promise.all([
        fetch(`/api/reports/active-plans?start_date=${params.start_date}&end_date=${params.end_date}&branch_id=${params.branch_id}`).then(r => r.json()),
        fetch(`/api/reports/payments-summary?start_date=${params.start_date}&end_date=${params.end_date}&branch_id=${params.branch_id}`).then(r => r.json()),
        fetch(`/api/reports/overdue-ageing?branch_id=${params.branch_id}`).then(r => r.json()),
        fetch(`/api/reports/daily-variance?start_date=${params.start_date}&end_date=${params.end_date}&branch_id=${params.branch_id}`).then(r => r.json()),
        fetch(`/api/reports/on-time-rate?start_date=${params.start_date}&end_date=${params.end_date}&branch_id=${params.branch_id}`).then(r => r.json()),
      ]);

      const endTime = performance.now();
      const queryTime = endTime - startTime;

      // Log query timing for observability
      console.log(`Dashboard queries completed in ${queryTime.toFixed(2)}ms`);

      const data = {
        activePlans: activePlans.data,
        paymentsSummary: paymentsSummary.data,
        overdueAgeing: overdueAgeing.data,
        dailyVariance: dailyVariance.data,
        onTimeRate: onTimeRate.data,
      };

      setDashboardData(data);

      // Cache the data
      dashboardCache.set('dashboard-data', data, 5 * 60 * 1000); // 5 minutes TTL

      // Update cache stats
      setCacheStats(dashboardCache.getStats());

    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Refresh data (bypass cache)
  const refreshData = () => {
    fetchDashboardData(false);
  };

  // Handle date range change
  const handleDateRangeChange = (field: 'start' | 'end', value: string) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  // Handle branch change
  const handleBranchChange = (branchId: string) => {
    setSelectedBranch(branchId);
  };

  // Clear cache
  const clearCache = () => {
    dashboardCache.clear();
    setCacheStats(dashboardCache.getStats());
  };

  // Generate KPI cards from data
  const generateKPICards = (): KPICard[] => {
    if (!dashboardData) return [];

    const { activePlans, paymentsSummary, overdueAgeing, dailyVariance, onTimeRate } = dashboardData;

    return [
      {
        id: 'active-agreements',
        title: 'الاتفاقيات النشطة',
        value: activePlans?.summary?.active_agreements || 0,
        change: 5.2, // This would come from historical data comparison
        changeType: 'increase',
        icon: DocumentTextIcon,
        color: 'bg-blue-500',
        description: 'إجمالي الاتفاقيات النشطة حالياً',
      },
      {
        id: 'total-payments',
        title: 'إجمالي المدفوعات',
        value: `د.ل ${(paymentsSummary?.summary?.total_amount || 0).toLocaleString()}`,
        change: 12.8,
        changeType: 'increase',
        icon: CurrencyDollarIcon,
        color: 'bg-green-500',
        description: 'إجمالي المدفوعات في الفترة المحددة',
      },
      {
        id: 'overdue-amount',
        title: 'المبلغ المتأخر',
        value: `د.ل ${(overdueAgeing?.total_outstanding || 0).toLocaleString()}`,
        change: -3.1,
        changeType: 'decrease',
        icon: ExclamationTriangleIcon,
        color: 'bg-red-500',
        description: 'إجمالي المبالغ المتأخرة',
      },
      {
        id: 'on-time-rate',
        title: 'معدل الدفع في الوقت',
        value: `${(onTimeRate?.summary?.on_time_rate_percentage || 0).toFixed(1)}%`,
        change: 2.4,
        changeType: 'increase',
        icon: CheckCircleIcon,
        color: 'bg-emerald-500',
        description: 'نسبة الأقساط المدفوعة في الوقت',
      },
      {
        id: 'daily-variance',
        title: 'متوسط الفروق اليومية',
        value: `د.ل ${Math.abs(dailyVariance?.summary?.avg_variance || 0).toFixed(2)}`,
        change: -1.2,
        changeType: 'decrease',
        icon: ChartBarIcon,
        color: 'bg-purple-500',
        description: 'متوسط الفروق في الإغلاقات اليومية',
      },
      {
        id: 'total-installments',
        title: 'إجمالي الأقساط',
        value: activePlans?.summary?.total_installments || 0,
        change: 8.7,
        changeType: 'increase',
        icon: ClockIcon,
        color: 'bg-orange-500',
        description: 'إجمالي عدد الأقساط في جميع الاتفاقيات',
      },
    ];
  };

  // Effect to fetch data when dependencies change
  useEffect(() => {
    if (isOnline) {
      fetchDashboardData();
    }
  }, [selectedBranch, dateRange.start, dateRange.end, isOnline]);

  // Effect to set up auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (isOnline) {
        fetchDashboardData();
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isOnline]);

  // Effect to get initial cache stats
  useEffect(() => {
    setCacheStats(dashboardCache.getStats());
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white p-6 rounded-lg shadow">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-red-800 mb-2">خطأ في تحميل البيانات</h3>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={refreshData}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              إعادة المحاولة
            </button>
          </div>
        </div>
      </div>
    );
  }

  const kpiCards = generateKPICards();

  return (
    <div className="min-h-screen bg-gray-50 p-6" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">لوحة المعلومات</h1>
            <p className="text-gray-600">نظرة عامة على أداء الأعمال</p>
          </div>
          
          <div className="flex items-center space-x-4 space-x-reverse mt-4 sm:mt-0">
            <button
              onClick={refreshData}
              disabled={!isOnline}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowPathIcon className="h-4 w-4 ml-2" />
              تحديث
            </button>
            
            <button
              onClick={clearCache}
              className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors"
            >
              مسح الذاكرة المؤقتة
            </button>
            
            <div className="flex items-center space-x-2 space-x-reverse">
              <FunnelIcon className="h-4 w-4 text-gray-400" />
              <select
                value={selectedBranch}
                onChange={(e) => handleBranchChange(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">جميع الفروع</option>
                <option value="branch1">الفرع الرئيسي</option>
                <option value="branch2">الفرع الثاني</option>
              </select>
            </div>
          </div>
        </div>

        {/* Cache Stats */}
        {cacheStats && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-blue-800">
                <span className="font-medium">الذاكرة المؤقتة:</span> {cacheStats.size} / {cacheStats.maxSize} عنصر
              </div>
              <div className="text-xs text-blue-600">
                آخر تحديث: {new Date().toLocaleTimeString('ar-SA')}
              </div>
            </div>
          </div>
        )}

        {/* Date Range Selector */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 sm:space-x-reverse">
            <label className="text-sm font-medium text-gray-700">الفترة الزمنية:</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => handleDateRangeChange('start', e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-gray-500">إلى</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => handleDateRangeChange('end', e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* KPI Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {kpiCards.map((card) => (
            <div key={card.id} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg ${card.color}`}>
                  <card.icon className="h-6 w-6 text-white" />
                </div>
                <div className="text-right">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    card.changeType === 'increase' 
                      ? 'bg-green-100 text-green-800' 
                      : card.changeType === 'decrease'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {card.changeType === 'increase' ? '+' : ''}{card.change}%
                  </span>
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{card.title}</h3>
              <p className="text-3xl font-bold text-gray-900 mb-2">{card.value}</p>
              <p className="text-sm text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>

        {/* Additional Dashboard Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">النشاط الأخير</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 space-x-reverse">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-sm text-gray-600">تم إنشاء اتفاقية جديدة</span>
                <span className="text-xs text-gray-400 mr-auto">منذ 5 دقائق</span>
              </div>
              <div className="flex items-center space-x-3 space-x-reverse">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span className="text-sm text-gray-600">تم تسجيل دفعة جديدة</span>
                <span className="text-xs text-gray-400 mr-auto">منذ 15 دقيقة</span>
              </div>
              <div className="flex items-center space-x-3 space-x-reverse">
                <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                <span className="text-sm text-gray-600">تم إغلاق اليوم</span>
                <span className="text-xs text-gray-400 mr-auto">منذ ساعة</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">إجراءات سريعة</h3>
            <div className="space-y-3">
              <button className="w-full text-right px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors">
                إنشاء اتفاقية جديدة
              </button>
              <button className="w-full text-right px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors">
                تسجيل دفعة
              </button>
              <button className="w-full text-right px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors">
                إغلاق اليوم
              </button>
              <button className="w-full text-right px-4 py-2 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors">
                تقرير المتأخرات
              </button>
            </div>
          </div>
        </div>

        {/* Offline Status */}
        {!isOnline && (
          <div className="fixed bottom-6 left-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 shadow-lg">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 ml-2" />
              <span className="text-sm text-yellow-800">أنت غير متصل بالإنترنت</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage; 